import React, { useState } from "react";
import styles from "./ClarificationOptions.module.css";
import { ClarificationData } from "../../api/models";

interface ClarificationOptionsProps {
  clarification: ClarificationData;
  assistantMessage: string;
  onSubmit: (originalQuery: string, selectedAnswers: Record<string, string>) => void;
  disabled?: boolean;
}

export const ClarificationOptions: React.FC<ClarificationOptionsProps> = ({
  clarification,
  assistantMessage,
  onSubmit,
  disabled = false,
}) => {
  // Track selected answer for each question
  const [selectedAnswers, setSelectedAnswers] = useState<Record<string, string>>({});

  const handleAnswerSelect = (questionIndex: number, answer: string) => {
    setSelectedAnswers(prev => ({
      ...prev,
      [questionIndex]: answer
    }));
  };

  const handleSubmit = () => {
    // Only submit if at least one answer is selected
    if (Object.keys(selectedAnswers).length > 0) {
      onSubmit(clarification.original_query, selectedAnswers);
    }
  };

  const allQuestionsAnswered = clarification.questions.every(
    (_, index) => selectedAnswers[index] !== undefined
  );

  return (
    <div className={styles.container}>
      <div className={styles.message}>{assistantMessage}</div>

      <div className={styles.questionsContainer}>
        {clarification.questions.map((q, questionIndex) => (
          <div key={questionIndex} className={styles.questionGroup}>
            <div className={styles.questionLabel}>{q.question}</div>
            <div className={styles.answersGrid}>
              {q.answers.map((answer, answerIndex) => (
                <button
                  key={answerIndex}
                  className={`${styles.answerButton} ${
                    selectedAnswers[questionIndex] === answer ? styles.selected : ""
                  }`}
                  onClick={() => handleAnswerSelect(questionIndex, answer)}
                  disabled={disabled}
                  type="button"
                >
                  {answer}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className={styles.footer}>
        <button
          className={styles.submitButton}
          onClick={handleSubmit}
          disabled={disabled || !allQuestionsAnswered}
          type="button"
        >
          <span>Get Answer</span>
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M3.5 8h9m0 0L9 4.5m3.5 3.5L9 11.5"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <span className={styles.hint}>Or type a more specific question below</span>
      </div>
    </div>
  );
};
